var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { useDropdownMenuContext } from "../dropdown-menu/dropdown-menu-context";
import { CheckIcon } from "../icon";
import { MenuItem } from "../menu-item";
import { fade, makeStyles } from "../styles";
import clsx from "../utils/clsx";
var dropdownMenuListItemComponentName = "QuantumDropdownMenuListItem";
export var useDropdownMenuItemStyles = makeStyles(function (theme) { return ({
    root: __assign(__assign({}, theme.typography.caption), { fontWeight: theme.typography.fontWeightRegular, color: theme.palette.text.secondary, height: "auto", display: "flex", alignItems: "center", width: "100%", padding: theme.spacing(0.5, 1), borderRadius: 6, transition: theme.transitions.create(["color", "background-color", "box-shadow"], {
            duration: theme.transitions.duration.shortest,
        }), "&:active,&:hover": {
            color: theme.palette.text.primary,
            backgroundColor: fade(theme.mixins.shadeOfColor("neutral", "lightest"), 0.5),
            boxShadow: "none",
        }, "&:focus:not(:active)": __assign({ backgroundColor: "transparent" }, theme.mixins.focusRing()), "&$selected": {
            color: theme.palette.primary.main,
            backgroundColor: "transparent",
            "& $startIcon": {
                color: theme.palette.primary.main,
            },
            "&:active,&:hover": {
                backgroundColor: fade(theme.mixins.shadeOfColor("neutral", "lightest"), 0.5),
                color: theme.mixins.shadeOfColor("purple", "dark"),
                "& $startIcon": {
                    color: "currentColor",
                },
            },
            "&$disabled": {
                color: theme.mixins.shadeOfColor("purple", "light"),
                backgroundColor: "transparent",
            },
        }, "&$disabled": {
            opacity: 1,
            color: theme.mixins.shadeOfColor("neutral", "base"),
        } }),
    sizeLarge: {
        padding: theme.spacing(0.75, 1.5),
    },
    detailed: {
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.text.primary,
    },
    disabled: {},
    colorDanger: {
        color: theme.palette.error.main,
        "& $startIcon, & $endIcon": {
            color: "currentColor",
        },
        "&:active,&:hover": {
            color: theme.mixins.shadeOfColor("red", "dark"),
            backgroundColor: theme.palette.error.light,
        },
        "&:focus:not(:active)": __assign({ backgroundColor: "transparent" }, theme.mixins.focusRing({ color: theme.palette.error.main })),
        "&:$disabled": {},
    },
    selected: {},
    content: {
        flex: 1,
        overflow: "hidden",
    },
    title: {
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    description: {
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    icon: {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: theme.typography.pxToRem(16),
        "& > svg": {
            height: "1em",
            width: "1em",
        },
    },
    startIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    endIcon: {
        marginLeft: theme.spacing(1),
        color: theme.mixins.shadeOfColor("neutral", "base"),
    },
    selectedIcon: {
        marginLeft: theme.spacing(1),
        color: theme.palette.primary.main,
    },
}); }, { name: dropdownMenuListItemComponentName });
export var DropdownMenuListItem = React.forwardRef(function (props, ref) {
    var classes = useDropdownMenuItemStyles(props);
    var ctx = useDropdownMenuContext();
    var _a = props.component, component = _a === void 0 ? "button" : _a, title = props.title, children = props.children, description = props.description, startIcon = props.startIcon, endIcon = props.endIcon, className = props.className, selected = props.selected, color = props.color, disabled = props.disabled, propClasses = props.classes, rootProps = __rest(props, ["component", "title", "children", "description", "startIcon", "endIcon", "className", "selected", "color", "disabled", "classes"]);
    var isDetailed = !!description;
    var titleContent = title || children;
    var ComponentProp = component;
    if (ComponentProp === "button" && rootProps.href) {
        ComponentProp = "a";
    }
    var buttonProps = {};
    if (ComponentProp === "button") {
        buttonProps.type = "button";
        buttonProps.disabled = disabled;
    }
    else {
        if (ComponentProp !== "a" || !rootProps.href) {
            buttonProps.role = "button";
        }
        buttonProps["aria-disabled"] = disabled;
    }
    var handleItemClick = function (event) {
        ctx.onSelectItem();
        if (rootProps.onClick) {
            rootProps.onClick(event);
        }
    };
    return (React.createElement(MenuItem, __assign({ selected: selected, onClick: handleItemClick, className: clsx(classes.root, ctx.size === "large" && classes.sizeLarge, isDetailed && classes.detailed, selected && classes.selected, color === "danger" && classes.colorDanger, disabled && classes.disabled, className), component: ComponentProp, disabled: disabled, ref: ref, title: typeof titleContent === "string" ? titleContent : undefined }, rootProps, buttonProps),
        !!startIcon && React.createElement("div", { className: clsx(classes.icon, classes.startIcon) }, startIcon),
        React.createElement("div", { className: classes.content },
            React.createElement("div", { className: classes.title }, titleContent),
            !!description && React.createElement("div", { className: classes.description }, description)),
        !!endIcon && React.createElement("div", { className: clsx(classes.icon, classes.endIcon) }, endIcon),
        !!selected && (React.createElement("div", { className: clsx(classes.icon, classes.selectedIcon) },
            React.createElement(CheckIcon, null)))));
});
